import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./ChicagoCitationContent.css";

// ---- tiny scroll-reveal wrapper (mirrors the reference design's fade-up-on-scroll) ----
const Reveal = ({ as: Tag = "div", className = "", style, children, id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      id={id}
      style={style}
      className={`chi-reveal${visible ? " chi-visible" : ""}${className ? ` ${className}` : ""}`}
    >
      {children}
    </Tag>
  );
};

const SwitchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M7 3v10a3 3 0 003 3h7M7 3L4 6M7 3l3 3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 21V11a3 3 0 00-3-3H7M17 21l3-3M17 21l-3-3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 20l1-4L16 5l3 3L8 19l-4 1z" strokeLinejoin="round" />
  </svg>
);

const HashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 8h14M5 16h14M10 4L8 20M16 4l-2 16" strokeLinecap="round" />
  </svg>
);

const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 5.5A2.5 2.5 0 016.5 3H12v18H6.5A2.5 2.5 0 014 18.5v-13z" strokeLinejoin="round" />
    <path d="M20 5.5A2.5 2.5 0 0017.5 3H12v18h5.5a2.5 2.5 0 002.5-2.5v-13z" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3v13m0 0l-5-5m5 5l5-5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 20h16" strokeLinecap="round" />
  </svg>
);

const CHI_STYLE_SYSTEMS = [
  { icon: "N-B", title: "Notes-Bibliography Style", desc: "Used in history, literature, and the arts. Sources are cited with a numbered footnote or endnote and gathered into a full bibliography at the end of the paper.", tags: ["Footnote generator", "Full bibliography"] },
  { icon: "A-D", title: "Author-Date Style", desc: "Common in the physical, natural, and social sciences. Sources appear as a short parenthetical citation in the text and a matching reference list at the end.", tags: ["Parenthetical citations", "Reference list"] },
  { icon: "IT", title: "Chicago In-Text Citations", desc: "Short author-date references placed directly inside a sentence, used when your paper follows the author-date system rather than footnotes.", tags: ["Author-date format", "Page number pairing"] },
  { icon: "FN", title: "Chicago Footnotes and Endnotes", desc: "Full or shortened notes numbered to match a superscript marker in the body text, formatted according to the notes-bibliography system.", tags: ["First full note", "Shortened repeat note"] },
];

const CHI_STEPS = [
  { title: "Choose Notes-Bibliography or Author-Date", desc: "Confirm which Chicago system your assignment calls for before you start entering sources." },
  { title: "Select the Identifier Type", desc: "Pick URL, DOI, ISBN, PMID, or Title, depending on what information you have for the source." },
  { title: "Paste the Source Details", desc: "Drop in the link, identifier, or title, and the generator pulls the author, date, and publisher." },
  { title: "Review the Citation", desc: "Check the author name order, capitalization, and punctuation against the source itself." },
  { title: "Copy the Note and the Bibliography Entry", desc: "Copy the footnote or in-text citation and its full bibliography or reference list entry together." },
];

const CHI_SOURCE_TYPES = [
  { tag: "Website", title: "Chicago Website Citation Generator", desc: "Cite webpages, blog posts, and online articles with the site name, publisher, and access date where required." },
  { tag: "Book", title: "Chicago Book Citation Generator", desc: "Format print books, edited collections, and ebooks with the correct edition and publisher details." },
  { tag: "Journal Article", title: "Chicago Journal Article Citation Generator", desc: "Generate citations for scholarly articles, including volume, issue, page range, and DOI." },
  { tag: "Video", title: "Chicago YouTube and Video Citation Generator", desc: "Cite recorded lectures, interviews, and online videos with the creator and upload date." },
  { tag: "Newspaper", title: "Chicago Newspaper Citation Generator", desc: "Format citations for print and online newspaper articles, including section and publication date." },
  { tag: "Report", title: "Chicago Report Citation Generator", desc: "Cite government, institutional, and organizational reports with the issuing body listed correctly." },
];

const CHI_EXAMPLES = [
  {
    top: ["Journal Article", "Notes-Bibliography"],
    body: 'Smith, Jane. "Understanding Research Habits Among Graduate Students." Journal of Higher Education Studies 33, no. 1 (2026): 115–139.',
    intext: 'Footnote: 1. Jane Smith, "Understanding Research Habits Among Graduate Students," Journal of Higher Education Studies 33, no. 1 (2026): 120.',
  },
  {
    top: ["Website", "Author-Date"],
    body: 'ResearchPal. 2026. "How AI Tools Help Students Write Better." ResearchPal Blog. researchpal.co.',
    intext: "In-text citation: (ResearchPal 2026)",
  },
];

const CHI_WHY_ITEMS = [
  { icon: <SwitchIcon />, title: "Notes and Author-Date Support", desc: "Switch between both Chicago systems depending on what your paper needs." },
  { icon: <GridIcon />, title: "Source-Type Fields", desc: "Fields adjust automatically for websites, books, journals, and reports." },
  { icon: <EditIcon />, title: "Manual Editing", desc: "Fix any missing detail by hand when a source page leaves information out." },
  { icon: <HashIcon />, title: "Footnote Numbering", desc: "Notes are numbered in order and formatted as full or shortened entries." },
  { icon: <BookIcon />, title: "Bibliography Output", desc: "Build a complete bibliography or reference list alongside your notes." },
  { icon: <DownloadIcon />, title: "Copy, Save, or Export", desc: "Send finished citations straight to Word, Google Docs, or your reference manager." },
];

const CHI_PLAN_COMPARISON = [
  { feature: "Generate notes-bibliography citations", free: true, premium: true },
  { feature: "Generate author-date citations", free: true, premium: true },
  { feature: "Copy citations instantly", free: true, premium: true },
  { feature: "Save bibliography lists", free: "limited", premium: "Unlimited" },
  { feature: "Export to Word or Google Docs", free: "limited", premium: true },
  { feature: "Bulk citation import", free: null, premium: true },
];

const CHI_FAQS = [
  { q: "What is a Chicago style citation generator?", a: "A Chicago style citation generator takes the details of a source, such as an author, title, and publication date, and arranges them according to the rules set out in the Chicago Manual of Style, producing a footnote, in-text citation, or bibliography entry without you having to format it by hand." },
  { q: "How does a citation generator for Chicago style work?", a: "You choose whether your paper follows the notes-bibliography or author-date system, enter a link, identifier, or title for your source, and the generator fills in the author, container, publisher, and date in the correct Chicago order and punctuation." },
  { q: "What is the Chicago Manual of Style and why does it matter for citations?", a: "The Chicago Manual of Style is a style guide used widely in history, literature, and the arts. It sets exact rules for how sources are numbered, punctuated, and listed, so citations stay consistent across an entire paper or publication." },
  { q: "How do I create a citation in Chicago format?", a: "Gather the author, title, publisher, and date of your source, decide whether you need a note or an author-date entry, then arrange those details in the order and punctuation the Chicago format specifies, or let a generator do the arranging for you." },
  { q: "Is there a difference between Chicago style and Chicago format?", a: "Chicago style refers to the overall guide, while Chicago format usually describes the specific arrangement of a citation, such as author order, italics, and punctuation. In practice the two terms are used interchangeably by most students and instructors." },
  { q: "Is this Chicago citation generator really free to use?", a: "Yes. You can generate Chicago style citations without a subscription. Some features, such as saving longer bibliography lists or exporting in bulk, are part of the premium plan, but basic citation generation stays free." },
  { q: "Can I generate more than one citation at a time?", a: "You can generate citations one source at a time in the free version. Premium accounts support bulk citation import for building a full bibliography from a list of sources at once." },
  { q: "How do I write a Chicago in-text citation?", a: "In the author-date system, a Chicago in-text citation appears in parentheses with the author's last name and the year of publication, and a page number when you reference a specific passage, for example (Smith 2026, 45)." },
  { q: "What does a Chicago style citation maker actually produce?", a: "A Chicago style citation maker produces the full note or bibliography entry for a source, along with the shortened in-text or footnote version, so both pieces stay consistent with each other throughout your paper." },
  { q: "How is a Chicago style in-text citation different from a footnote?", a: "An in-text citation is a short parenthetical reference used in the author-date system, while a footnote is a numbered note placed at the bottom of the page and used in the notes-bibliography system. Both point back to a full entry in your reference list or bibliography." },
  { q: "What are the two systems within the Chicago citation style?", a: "Chicago style includes the notes-bibliography system, common in history and the humanities, and the author-date system, common in the sciences and social sciences. Each uses a different way of marking a citation in the body text." },
  { q: "What is the correct Chicago style citation format for a book?", a: "A Chicago bibliography entry for a book lists the author's last name first, followed by the first name, the book title in italics, the city of publication, the publisher, and the year, each separated according to Chicago punctuation rules." },
  { q: "Can a Chicago citation maker handle websites and online sources?", a: "Yes. A website citation in Chicago style includes the author or organization, the page title, the site name, and either the publication date or the date you accessed the page, depending on whether the site lists a clear publication date." },
  { q: "How to Chicago style citation a source with no listed author?", a: "When no individual author is listed, Chicago style allows you to begin the citation with the organization responsible for the content, or with the title of the source itself if no organization is given." },
  { q: "What information do I need before I create a Chicago style citation?", a: "Before creating a citation, collect the author or organization, the title, the publisher or container, the publication date, and, for print sources, the page range or page number you are referencing." },
  { q: "How to do a Chicago style citation for a journal article?", a: "A Chicago journal article citation lists the author, the article title in quotation marks, the journal name in italics, the volume and issue number, the year, and the page range, followed by a DOI if one is available." },
  { q: "Do I need to number my footnotes manually?", a: "No. The generator numbers each footnote or endnote in the order your sources appear in the text, and keeps the shortened repeat notes matched to the correct full note further up the page." },
  { q: "Can I save or export my Chicago bibliography?", a: "Free accounts can copy citations directly into a document. Premium accounts can save longer bibliography lists and export them straight to Word or Google Docs." },
];

const FAQ_BATCH = 5;

const ChiFaqList = () => {
  const [revealed, setRevealed] = useState(FAQ_BATCH);
  const [openSet, setOpenSet] = useState(() => new Set());

  const toggle = (idx) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const visible = CHI_FAQS.slice(0, revealed);
  const remaining = CHI_FAQS.length - revealed;

  return (
    <>
      <div className="chi-faq-wrap">
        {visible.map((item, idx) => {
          const open = openSet.has(idx);
          return (
            <div className={`chi-faq-item${open ? " chi-open" : ""}`} key={item.q}>
              <div className="chi-faq-q" onClick={() => toggle(idx)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggle(idx); }}>
                <span>{item.q}</span>
                <span className="chi-plus">+</span>
              </div>
              <div className="chi-faq-a"><p>{item.a}</p></div>
            </div>
          );
        })}
      </div>
      {remaining > 0 && (
        <div className="chi-load-more-wrap">
          <button type="button" className="chi-btn chi-btn-ghost" onClick={() => setRevealed((prev) => Math.min(prev + FAQ_BATCH, CHI_FAQS.length))}>
            Show More Questions (<span className="chi-hidden-count">{remaining}</span> remaining)
          </button>
        </div>
      )}
    </>
  );
};

const ChicagoCitationContent = () => {
  return (
    <div className="chi-lp">
      <section className="chi-intro">
        <Reveal as="span" className="chi-eyebrow">Chicago Manual of Style, 17th Edition</Reveal>
        <Reveal as="h1">Free Chicago Citation Generator: Notes-Bibliography & Author-Date</Reveal>
        <Reveal as="p">Turn a link, a title, or a set of publication details into a properly formatted Chicago citation. This Chicago style citation generator builds full bibliography entries, shortened notes, and in-text references so your paper follows the Chicago Manual of Style without you memorizing every rule yourself.</Reveal>
        <Reveal>
          <a href="#citation-tool" className="chi-btn chi-btn-primary">Generate My Chicago Citation</a>
        </Reveal>
        <Reveal as="div" className="chi-pill-row">
          <div className="chi-pill"><span className="chi-dot" />Notes-bibliography and author-date support</div>
          <div className="chi-pill"><span className="chi-dot" />Footnote and endnote numbering</div>
          <div className="chi-pill"><span className="chi-dot" />In-text citations built in</div>
          <div className="chi-pill"><span className="chi-dot" />Copy, save, or export your list</div>
        </Reveal>
      </section>

      <section className="chi-section chi-band-mint" id="chi-what-is">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">The Basics</span>
            <h2>What Is a Chicago Style Citation?</h2>
          </Reveal>
          <Reveal className="chi-quote-panel">
            <p>A Chicago style citation records where a piece of information came from, in the order and punctuation the Chicago Manual of Style sets out, so a reader can trace a quote, a fact, or an idea back to its original source.</p>
          </Reveal>
          <div className="chi-two-col">
            <Reveal className="chi-info-card">
              <h3>How Chicago Citations Work</h3>
              <p>Instead of guessing at spacing and order, you enter the author, title, publisher, and date of your source, and the generator arranges them according to whichever Chicago system your course or publisher requires, either notes-bibliography or author-date.</p>
            </Reveal>
            <Reveal className="chi-info-card">
              <h3>Why It Matters for Your Paper</h3>
              <p>History, art, and many humanities departments grade citation formatting directly. A consistent Chicago style citation format keeps your footnotes, endnotes, and bibliography aligned, and it gives proper credit to every source you build your argument on.</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="chi-section" id="chi-styles">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">Pick Your System</span>
            <h2>Which Chicago Citation Style Do You Need?</h2>
            <p>The Chicago Manual of Style covers two separate citation systems. Choose the one your instructor, journal, or department asks for.</p>
          </Reveal>
          <div className="chi-style-grid">
            {CHI_STYLE_SYSTEMS.map((s) => (
              <Reveal key={s.title} className="chi-style-card">
                <div className="chi-style-card-top">
                  <div className="chi-style-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                </div>
                <p>{s.desc}</p>
                <div className="chi-tag-row">{s.tags.map((t) => <span className="chi-tag-chip" key={t}>{t}</span>)}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="chi-section-tight chi-band-mint" id="chi-how-to">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">Five Simple Steps</span>
            <h2>How to Do a Chicago Style Citation</h2>
          </Reveal>
          <div className="chi-steps-list">
            {CHI_STEPS.map((step, idx) => (
              <Reveal className="chi-step-row" key={step.title}>
                <div className="chi-step-num">{idx + 1}</div>
                <div className="chi-step-copy"><h4>{step.title}</h4><p>{step.desc}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="chi-section" id="chi-sources">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">By Source</span>
            <h2>Generate Chicago Citations by Source Type</h2>
            <p>Different sources carry different details. Pick the type that matches what you are citing.</p>
          </Reveal>
          <div className="chi-source-grid">
            {CHI_SOURCE_TYPES.map((item) => (
              <Reveal key={item.tag} className="chi-source-card">
                <span className="chi-eyebrow">{item.tag}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="chi-section chi-band-mint" id="chi-examples">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">See It in Action</span>
            <h2>Example Chicago Citations</h2>
          </Reveal>
          <div className="chi-example-grid">
            {CHI_EXAMPLES.map((ex) => (
              <Reveal key={ex.body} className="chi-example-card">
                <div className="chi-example-top"><span>{ex.top[0]}</span><span>{ex.top[1]}</span></div>
                <div className="chi-example-body">
                  <p>{ex.body}</p>
                  <p className="chi-example-intext">{ex.intext}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="chi-section" id="chi-why">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">The Payoff</span>
            <h2>Why Use Our Chicago Citation Generator?</h2>
          </Reveal>
          <div className="chi-feature-grid">
            {CHI_WHY_ITEMS.map((w) => (
              <Reveal key={w.title} className="chi-feature-item">
                <div className="chi-feature-icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="chi-section chi-band-mint" id="chi-pricing">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">Compare Plans</span>
            <h2>Free and Premium Features</h2>
          </Reveal>
          <Reveal className="chi-price-table">
            <div className="chi-price-row chi-head"><span>Feature</span><span>Free Version</span><span>Premium Version</span></div>
            {CHI_PLAN_COMPARISON.map((row) => (
              <div className="chi-price-row" key={row.feature}>
                <span>{row.feature}</span>
                <span className={row.free === true ? "chi-check" : row.free === "limited" ? "chi-limited" : "chi-dash"}>
                  {row.free === true ? "✓" : row.free === "limited" ? "Limited" : "—"}
                </span>
                <span className="chi-check">
                  {row.premium === true ? "✓" : row.premium}
                </span>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="chi-section" id="chi-ai-research-assistant">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">AI Research Assistant</span>
            <h2>AI Research Assistant for Chicago-Style Research</h2>
          </Reveal>
          <div className="chi-two-col">
            <Reveal className="chi-info-card">
              <p style={{ marginBottom: "14px" }}>Chicago style can involve either the notes-bibliography system or the author-date system, making it important to understand how a source should be used before formatting it.</p>
              <p style={{ marginBottom: "14px" }}>ResearchPal's free Chicago Citation Generator supports Chicago-style references, including notes, bibliography and author-date workflows.</p>
              <p>The ResearchPal AI Research Assistant complements this process by helping you investigate the research itself. You can ask questions about a source, explore historical or academic context, clarify concepts, and think through how evidence connects to your research before creating your citation.</p>
            </Reveal>
            <Reveal className="chi-info-card">
              <h3>Go Beyond Chicago Citation Formatting</h3>
              <p style={{ marginBottom: "10px" }}>The AI Research Assistant can help you:</p>
              <ul className="chi-ai-list">
                <li>Understand a source before citing it</li>
                <li>Explore the context behind academic evidence</li>
                <li>Clarify research questions</li>
                <li>Discuss findings and interpretations</li>
                <li>Organize your thinking around a research topic</li>
                <li>Identify what additional evidence you may need</li>
              </ul>
            </Reveal>
          </div>
          <Reveal className="chi-ai-cta">
            <Link to="/ai-agent" className="chi-btn chi-btn-primary">Try AI Research Assistant</Link>
          </Reveal>
        </div>
      </section>

      <section className="chi-section" id="chi-free-tools">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">More Free Tools</span>
            <h2>Explore ResearchPal's Free Tools</h2>
          </Reveal>

          <div className="chi-free-tools-group">
            <h3 className="chi-free-tools-group-title">ResearchPal Free Tools</h3>
            <Reveal className="chi-source-grid">
              <Link className="chi-free-tool-card" to="/paraphraser">
                <div><h3>Paraphraser</h3><p>Rewrite sentences and paragraphs while keeping the meaning.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
              <Link className="chi-free-tool-card" to="/ai-agent">
                <div><h3>AI Agent</h3><p>Chat with an AI assistant built for research tasks.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
              <Link className="chi-free-tool-card" to="/search">
                <div><h3>Search Papers</h3><p>Find and explore academic papers across sources.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
              <Link className="chi-free-tool-card" to="/essay-writer">
                <div><h3>Essay Writer</h3><p>Draft structured essays with AI assistance.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
              <Link className="chi-free-tool-card" to="/literature-review">
                <div><h3>Literature Review</h3><p>Summarize and organize sources for your review.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
              <Link className="chi-free-tool-card" to="/paper-insights">
                <div><h3>Paper Insights</h3><p>Get quick, structured insights from any paper.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>

          <div className="chi-free-tools-group">
            <h3 className="chi-free-tools-group-title">Other Free Citation Generators</h3>
            <Reveal className="chi-source-grid">
              <Link className="chi-free-tool-card" to="/citation-generator/apa">
                <div><h3>APA Citation Generator</h3><p>Format references in APA style instantly.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
              <Link className="chi-free-tool-card" to="/citation-generator/harvard">
                <div><h3>Harvard Citation Generator</h3><p>Create author-date Harvard references.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
              <Link className="chi-free-tool-card" to="/citation-generator/mla">
                <div><h3>MLA Citation Generator</h3><p>Generate MLA in-text and Works Cited entries.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
              <Link className="chi-free-tool-card" to="/citation-generator/bibtex">
                <div><h3>BibTeX Generator</h3><p>Get ready-to-use BibTeX entries for LaTeX.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
              <Link className="chi-free-tool-card" to="/citation-generator/metadata">
                <div><h3>Metadata Lookup</h3><p>View a source's structured details before citing it.</p></div>
                <span className="chi-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="chi-cta-band">
        <Reveal>
          <h2>Ready to Generate Your Chicago Citation?</h2>
          <p>Paste a link, DOI, or title and get a properly formatted Chicago citation in seconds, with no sign-up required to try.</p>
          <a href="#citation-tool" className="chi-btn chi-btn-primary">Generate My Chicago Citation Free</a>
        </Reveal>
      </section>

      <section className="chi-section" id="chi-faq">
        <div className="chi-container">
          <Reveal className="chi-section-head">
            <span className="chi-eyebrow">Frequently Asked</span>
            <h2>Chicago Citation Generator FAQs</h2>
            <p>Answers to the questions students and researchers ask most about Chicago style citations.</p>
          </Reveal>
          <ChiFaqList />
        </div>
      </section>
    </div>
  );
};

export default ChicagoCitationContent;
